/**
 * Device types and devices (migration 20261002170000). Staff-only tables; the
 * admin session reads them under `is_paznetworks_staff()`.
 *
 * The catalog (manufacturers → types → templates) is small and read whole, then
 * joined in memory, so no query depends on PostgREST embeds. Devices are read
 * per site because staff can read every site's rows.
 */
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { supabase } from '../lib/supabase';
import type {
  ComponentKind,
  DeviceConfidence,
  DeviceStatus,
  DeviceTypeKind,
  TemplateInsert,
  TemplateRow,
} from '../lib/dcim';

export interface ManufacturerRow { id: string; name: string; description: string | null }
export interface PlatformRow { id: string; name: string; manufacturer_id: string | null; description: string | null }
export interface DeviceRoleRow { id: string; name: string; color_token: string; description: string | null }
export interface ModuleTypeProfileRow { id: string; name: string; description: string | null }
export interface DeviceTypeRow {
  id: string;
  manufacturer_id: string;
  model: string;
  part_number: string | null;
  kind: DeviceTypeKind;
  u_height: number;
  is_full_depth: boolean;
  airflow: string | null;
  weight_kg: number | null;
  subdevice_role: 'parent' | 'child' | null;
  default_platform_id: string | null;
  description: string | null;
}
export interface ModuleTypeRow {
  id: string;
  manufacturer_id: string;
  profile_id: string | null;
  model: string;
  part_number: string | null;
  description: string | null;
}

export interface DcimCatalog {
  manufacturers: ManufacturerRow[];
  platforms: PlatformRow[];
  roles: DeviceRoleRow[];
  profiles: ModuleTypeProfileRow[];
  deviceTypes: DeviceTypeRow[];
  moduleTypes: ModuleTypeRow[];
  templates: TemplateRow[];
  manufacturerById: Map<string, ManufacturerRow>;
  /** device type id or module type id → its templates. */
  templatesByOwner: Map<string, TemplateRow[]>;
  /** device type id → number of devices built from it. */
  deviceCountByType: Map<string, number>;
}

const TEMPLATE_COLUMNS =
  'id, device_type_id, module_type_id, kind, name, label, type, description, mgmt_only, poe_mode, maximum_draw_w, allocated_draw_w, feed_leg, power_port_template_id, positions, rear_port_template_id, rear_port_position, position, sort_order';

export function useDcimCatalog() {
  return useQuery<DcimCatalog>({
    queryKey: ['dcim-catalog'],
    queryFn: async () => {
      const [m, p, r, pr, dt, mt, t, d] = await Promise.all([
        supabase.from('manufacturers').select('id, name, description').order('name'),
        supabase.from('platforms').select('id, name, manufacturer_id, description').order('name'),
        supabase.from('device_roles').select('id, name, color_token, description').order('name'),
        supabase.from('module_type_profiles').select('id, name, description').order('name'),
        supabase
          .from('device_types')
          .select('id, manufacturer_id, model, part_number, kind, u_height, is_full_depth, airflow, weight_kg, subdevice_role, default_platform_id, description')
          .order('model'),
        supabase.from('module_types').select('id, manufacturer_id, profile_id, model, part_number, description').order('model'),
        supabase.from('dcim_component_templates').select(TEMPLATE_COLUMNS),
        supabase.from('dcim_devices').select('device_type_id').is('deleted_at', null),
      ]);
      for (const res of [m, p, r, pr, dt, mt, t, d]) if (res.error) throw res.error;

      const manufacturers = (m.data ?? []) as ManufacturerRow[];
      const templates = (t.data ?? []) as TemplateRow[];
      const templatesByOwner = new Map<string, TemplateRow[]>();
      for (const row of templates) {
        const owner = row.device_type_id ?? row.module_type_id!;
        templatesByOwner.set(owner, [...(templatesByOwner.get(owner) ?? []), row]);
      }
      const deviceCountByType = new Map<string, number>();
      for (const row of d.data ?? []) deviceCountByType.set(row.device_type_id, (deviceCountByType.get(row.device_type_id) ?? 0) + 1);

      return {
        manufacturers,
        platforms: (p.data ?? []) as PlatformRow[],
        roles: (r.data ?? []) as DeviceRoleRow[],
        profiles: (pr.data ?? []) as ModuleTypeProfileRow[],
        deviceTypes: (dt.data ?? []).map((x) => ({ ...x, u_height: Number(x.u_height) })) as DeviceTypeRow[],
        moduleTypes: (mt.data ?? []) as ModuleTypeRow[],
        templates,
        manufacturerById: new Map(manufacturers.map((x) => [x.id, x])),
        templatesByOwner,
        deviceCountByType,
      };
    },
    staleTime: 60 * 1000,
  });
}

function useInvalidate(...keys: string[]) {
  const qc = useQueryClient();
  return () => keys.forEach((k) => qc.invalidateQueries({ queryKey: [k] }));
}

export function useCreateManufacturer() {
  const done = useInvalidate('dcim-catalog');
  return useMutation({
    mutationFn: async (row: { name: string; description?: string | null }) => {
      const { error } = await supabase.from('manufacturers').insert(row);
      if (error) throw error;
    },
    onSuccess: done,
  });
}

export function useCreatePlatform() {
  const done = useInvalidate('dcim-catalog');
  return useMutation({
    mutationFn: async (row: { name: string; manufacturer_id?: string | null; description?: string | null }) => {
      const { error } = await supabase.from('platforms').insert(row);
      if (error) throw error;
    },
    onSuccess: done,
  });
}

export function useCreateDeviceRole() {
  const done = useInvalidate('dcim-catalog');
  return useMutation({
    mutationFn: async (row: { name: string; color_token: string; description?: string | null }) => {
      const { error } = await supabase.from('device_roles').insert(row);
      if (error) throw error;
    },
    onSuccess: done,
  });
}

export type DeviceTypeInsert = Omit<DeviceTypeRow, 'id'>;

export function useCreateDeviceType() {
  const done = useInvalidate('dcim-catalog');
  return useMutation({
    mutationFn: async (row: DeviceTypeInsert): Promise<string> => {
      const { data, error } = await supabase.from('device_types').insert(row).select('id').single();
      if (error) throw error;
      return data.id;
    },
    onSuccess: done,
  });
}

export function useCreateModuleType() {
  const done = useInvalidate('dcim-catalog');
  return useMutation({
    mutationFn: async (row: Omit<ModuleTypeRow, 'id'>): Promise<string> => {
      const { data, error } = await supabase.from('module_types').insert(row).select('id').single();
      if (error) throw error;
      return data.id;
    },
    onSuccess: done,
  });
}

export function useAddTemplates() {
  const done = useInvalidate('dcim-catalog');
  return useMutation({
    mutationFn: async (rows: TemplateInsert[]) => {
      const { error } = await supabase.from('dcim_component_templates').insert(rows);
      if (error) throw error;
    },
    onSuccess: done,
  });
}

export function useDeleteTemplate() {
  const done = useInvalidate('dcim-catalog');
  return useMutation({
    mutationFn: async (id: string) => {
      const { error } = await supabase.from('dcim_component_templates').delete().eq('id', id);
      if (error) throw error;
    },
    onSuccess: done,
  });
}

// ─── Devices ────────────────────────────────────────────────────────────────

export interface DcimDeviceRow {
  id: string;
  location_id: string;
  name: string;
  device_type_id: string;
  role_id: string;
  platform_id: string | null;
  status: DeviceStatus;
  confidence: DeviceConfidence;
  serial: string | null;
  asset_tag: string | null;
  primary_ip: string | null;
  rack_unit_id: string | null;
  asset_id: string | null;
  observed_device_id: string | null;
  description: string | null;
  updated_at: string;
}

const DEVICE_COLUMNS =
  'id, location_id, name, device_type_id, role_id, platform_id, status, confidence, serial, asset_tag, primary_ip, rack_unit_id, asset_id, observed_device_id, description, updated_at';

export function useDcimDevices(locationId: string | undefined) {
  return useQuery<DcimDeviceRow[]>({
    queryKey: ['dcim-devices', locationId],
    enabled: Boolean(locationId),
    queryFn: async () => {
      const { data, error } = await supabase
        .from('dcim_devices')
        .select(DEVICE_COLUMNS)
        .eq('location_id', locationId!)
        .is('deleted_at', null)
        .order('name');
      if (error) throw error;
      return (data ?? []) as unknown as DcimDeviceRow[];
    },
  });
}

export interface ComponentRow {
  id: string;
  device_id: string;
  module_id: string | null;
  template_id: string | null;
  kind: ComponentKind;
  name: string;
  label: string | null;
  type: string | null;
  enabled: boolean;
  mgmt_only: boolean;
  poe_mode: string | null;
  maximum_draw_w: number | null;
  feed_leg: string | null;
  power_port_id: string | null;
  positions: number | null;
  rear_port_id: string | null;
  rear_port_position: number | null;
  position: string | null;
  installed_device_id: string | null;
  sort_order: number;
}
export interface ModuleRow {
  id: string;
  device_id: string;
  module_bay_id: string;
  module_type_id: string;
  status: DeviceStatus;
  confidence: DeviceConfidence;
  serial: string | null;
}
export interface MacRow { id: string; component_id: string; mac_address: string; is_primary: boolean }

export interface DcimDeviceDetail {
  components: ComponentRow[];
  modules: ModuleRow[];
  macsByComponent: Map<string, MacRow[]>;
}

const COMPONENT_COLUMNS =
  'id, device_id, module_id, template_id, kind, name, label, type, enabled, mgmt_only, poe_mode, maximum_draw_w, feed_leg, power_port_id, positions, rear_port_id, rear_port_position, position, installed_device_id, sort_order';

export function useDcimDeviceDetail(deviceId: string | null) {
  return useQuery<DcimDeviceDetail>({
    queryKey: ['dcim-device', deviceId],
    enabled: Boolean(deviceId),
    queryFn: async () => {
      const [c, mo] = await Promise.all([
        supabase.from('dcim_device_components').select(COMPONENT_COLUMNS).eq('device_id', deviceId!),
        supabase.from('dcim_modules').select('id, device_id, module_bay_id, module_type_id, status, confidence, serial').eq('device_id', deviceId!),
      ]);
      if (c.error) throw c.error;
      if (mo.error) throw mo.error;
      const components = (c.data ?? []) as ComponentRow[];
      const interfaceIds = components.filter((x) => x.kind === 'interface').map((x) => x.id);
      const macsByComponent = new Map<string, MacRow[]>();
      if (interfaceIds.length) {
        const macs = await supabase.from('dcim_mac_addresses').select('id, component_id, mac_address, is_primary').in('component_id', interfaceIds);
        if (macs.error) throw macs.error;
        for (const row of (macs.data ?? []) as unknown as MacRow[]) {
          macsByComponent.set(row.component_id, [...(macsByComponent.get(row.component_id) ?? []), row]);
        }
      }
      return { components, modules: (mo.data ?? []) as ModuleRow[], macsByComponent };
    },
  });
}

export interface DeviceInsert {
  location_id: string;
  name: string;
  device_type_id: string;
  role_id: string;
  platform_id: string | null;
  status: DeviceStatus;
  confidence: DeviceConfidence;
  serial: string | null;
  asset_tag: string | null;
  primary_ip: string | null;
  rack_unit_id: string | null;
  description: string | null;
}

/** Inserting the row is enough: the database copies the type's templates into components. */
export function useCreateDevice() {
  const done = useInvalidate('dcim-devices', 'dcim-catalog');
  return useMutation({
    mutationFn: async (row: DeviceInsert): Promise<string> => {
      const { data, error } = await supabase.from('dcim_devices').insert(row).select('id').single();
      if (error) throw error;
      return data.id;
    },
    onSuccess: done,
  });
}

export function useUpdateDevice() {
  const done = useInvalidate('dcim-devices');
  return useMutation({
    mutationFn: async ({ id, patch }: { id: string; patch: Partial<Omit<DeviceInsert, 'device_type_id' | 'location_id'>> }) => {
      const { error } = await supabase.from('dcim_devices').update(patch).eq('id', id);
      if (error) throw error;
    },
    onSuccess: done,
  });
}

/** Soft delete; components stay for the audit trail until a hard delete. */
export function useRetireDevice() {
  const done = useInvalidate('dcim-devices', 'dcim-catalog');
  return useMutation({
    mutationFn: async (id: string) => {
      const { error } = await supabase.from('dcim_devices').update({ deleted_at: new Date().toISOString() }).eq('id', id);
      if (error) throw error;
    },
    onSuccess: done,
  });
}

export function useInstallModule() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (row: { device_id: string; module_bay_id: string; module_type_id: string; serial: string | null }) => {
      const { error } = await supabase.from('dcim_modules').insert(row);
      if (error) throw error;
    },
    onSuccess: (_d, v) => qc.invalidateQueries({ queryKey: ['dcim-device', v.device_id] }),
  });
}

export function useRemoveModule() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async ({ id }: { id: string; deviceId: string }) => {
      const { error } = await supabase.from('dcim_modules').delete().eq('id', id);
      if (error) throw error;
    },
    onSuccess: (_d, v) => qc.invalidateQueries({ queryKey: ['dcim-device', v.deviceId] }),
  });
}

export function useAddMac() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (row: { component_id: string; mac_address: string; is_primary: boolean; deviceId: string }) => {
      const { error } = await supabase
        .from('dcim_mac_addresses')
        .insert({ component_id: row.component_id, mac_address: row.mac_address, is_primary: row.is_primary });
      if (error) throw error;
    },
    onSuccess: (_d, v) => qc.invalidateQueries({ queryKey: ['dcim-device', v.deviceId] }),
  });
}
